import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import TextBox from "./TextBox";
import Sidebar2 from "./Sidebar2";
import { analyzeContent } from "./analyzeContent";
import { generateContent } from "./generateContent";
import { getArticleVersion } from "../../lib/articles";
import { listProjectsForUser } from "../../lib/projects";

const LayoutWrapper = styled.div`
  display: flex;
  min-width: 0;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  align-items: stretch;
`;

const spin = keyframes`
   to { transform: rotate(360deg); }
 `;
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;
const Box = styled.div`
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 12px;
`;
const Spinner = styled.div`
  width: 22px;
  height: 22px;
  border: 3px solid #d1d5db;
  border-top-color: #2f855a;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;
const Msg = styled.div`
  color: #111827;
  font-weight: 600;
`;

const ContentGenerator = ({
  profile,
  currentUserRole,
  feedback,
  setFeedback,
  organization,
  onUnsavedDraftChange,
}) => {
  //console.log("User role in CG: ", currentUserRole);
  const [content, setContent] = useState("");

  const [currentArticleId, setCurrentArticleId] = useState(null);
  const [pendingVersion, setPendingVersion] = useState(null);
  // loading overlay
  const [isGenerating, setIsGenerating] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(
    "Generating your article..."
  );
  const [projects, setProjects] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const article = location.state?.openArticle;
    if (!article) return;

    // fire the same event FileManager uses
    window.dispatchEvent(
      new CustomEvent("filemanager:openArticle", { detail: article })
    );

    // optional: clear state so refresh doesn't re-trigger
    // navigate(location.pathname, { replace: true, state: {} });
  }, [location.state]);

  // useEffect(() => {
  //   const savedFeedback = localStorage.getItem("feedbackGrades");
  //   if (savedFeedback) {
  //     setFeedback(JSON.parse(savedFeedback)); // Parse and set the saved feedback
  //   }
  // }, []);

  // // Save feedback to localStorage whenever it changes
  // useEffect(() => {
  //   localStorage.setItem("feedbackGrades", JSON.stringify(feedback));
  // }, [feedback]);

  useEffect(() => {
    const article = location.state?.openArticle;
    if (!article) return;
    // hand off to TextBox via a tiny DOM event
    window.dispatchEvent(
      new CustomEvent("filemanager:openArticle", { detail: article })
    );
    // clear the state so a back/forward doesn’t re-open again
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  useEffect(() => {
    if (!profile?.id || !profile?.company_id) return;
    (async () => {
      // load projects allowed for this user (Admin gets all)
      const list = await listProjectsForUser({
        user_id: profile.id,
        role: profile.role,
        company_id: profile.company_id,
      });
      setProjects(list || []);
    })();
  }, [profile?.id, profile?.role, profile?.company_id]);

  const handleOpenVersion = async (versionId) => {
    try {
      const v = await getArticleVersion(versionId);
      // hand off to TextBox via prop
      setPendingVersion(v);
    } catch (e) {
      console.error("Failed to load version:", e);
      alert("Failed to load version.");
    }
  };

  const improvementKeyMap = {
    overall: "Overall",
    readability: "Readability",
    topicAuthority: "Topic Authority",
    contentValue: "Content Value",
    seo: "SEO",
  };

  const handleImprovementApplied = ({ categoryKey, index }) => {
    setFeedback((prev) => {
      if (!prev || !prev.improvements) return prev;

      const mappedKey = improvementKeyMap[categoryKey] || categoryKey;
      const currentArr = prev.improvements[mappedKey] || [];

      const newArr = currentArr.filter((_, i) => i !== index);

      return {
        ...prev,
        improvements: {
          ...prev.improvements,
          [mappedKey]: newArr,
        },
      };
    });
  };

  return (
    <>
      <LayoutWrapper>
        <TextBox
          setContent={setContent}
          analyzeContent={analyzeContent}
          generateContent={generateContent}
          setFeedback={setFeedback}
          organization={organization}
          feedback={feedback}
          profile={profile}
          projects={projects}
          beginGenerating={(msg = "Generating your article...") => {
            setLoadingMessage(msg);
            setIsGenerating(true);
          }}
          endGenerating={() => setIsGenerating(false)}
          currentUserRole={currentUserRole}
          onArticleContextChange={(ctx) => {
            // ctx: { id: string|null, title: string }
            setCurrentArticleId(ctx?.id ?? null);
          }}
          onUnsavedDraftChange={onUnsavedDraftChange}
          pendingVersion={pendingVersion}
          onVersionApplied={() => setPendingVersion(null)}
          onImprovementApplied={handleImprovementApplied}
        />
        <Sidebar2
          feedback={feedback}
          currentArticleId={currentArticleId}
          onOpenVersion={handleOpenVersion}
          currentUserRole={currentUserRole}
          organization={organization}
        />
      </LayoutWrapper>
      {isGenerating && (
        <Overlay>
          <Box>
            <Spinner />
            <Msg>{loadingMessage}</Msg>
          </Box>
        </Overlay>
      )}
    </>
  );
};

export default ContentGenerator;
